import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { Job, JobList, JobsState } from 'types/jobs';

import { JobStatus, RequestStatus } from 'constants/enums';
import { FALLBACK_NUMBER_VALUE } from 'constants/uiSettings';

import {
  fetchActiveJobDetails,
  fetchFreeJobsInfo,
  fetchJobsData,
  fetchJobsForDashboard
} from './jobsThunks';

const initialState: JobsState = {
  jobs: [],
  status: RequestStatus.Idle,
  error: null,
  count: 0,
  activeJobDetails: null,
  freeJobsLeft: 0,
  dashboardJobs: []
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    addJob: (state, action: PayloadAction<Job>) => {
      state.jobs = [...state.jobs, action.payload];
    },
    upsertJob: (state, action: PayloadAction<Job>) => {
      const index = state.jobs.findIndex((job) => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = { ...state.jobs[index], ...action.payload };
      } else {
        state.jobs.push(action.payload);
      }
    },
    dropJobs: (state) => {
      state.jobs = [];
      state.count = 0;
      state.activeJobDetails = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobsData.pending, (state) => {
        state.status = RequestStatus.Loading;
      })
      .addCase(
        fetchJobsData.fulfilled,
        (state, action: PayloadAction<JobList>) => {
          state.status = RequestStatus.Succeeded;
          state.jobs = action.payload.content;
          state.count = action.payload.totalElements;
          state.error = null;
        }
      )
      .addCase(fetchJobsData.rejected, (state, action) => {
        state.status = RequestStatus.Failed;
        state.error = action.error.message || null;
      })
      .addCase(
        fetchActiveJobDetails.fulfilled,
        (state, action: PayloadAction<Job>) => {
          state.activeJobDetails = action.payload;
        }
      )
      .addCase(fetchActiveJobDetails.rejected, (state) => {
        state.activeJobDetails = null;
      });
    builder.addCase(fetchFreeJobsInfo.fulfilled, (state, action) => {
      state.freeJobsLeft = action.payload.freeJobsLeft;
    });
    builder.addCase(fetchFreeJobsInfo.rejected, (state) => {
      state.freeJobsLeft = FALLBACK_NUMBER_VALUE;
    });
    builder.addCase(fetchJobsForDashboard.pending, (state) => {
      state.status = RequestStatus.Loading;
    });
    builder.addCase(fetchJobsForDashboard.fulfilled, (state, action) => {
      const sortedByActiveStatus = [...(action.payload.content || [])].sort(
        (a, b) =>
          a.status === JobStatus.Active && b.status !== JobStatus.Active
            ? -1
            : 1
      );
      state.dashboardJobs = sortedByActiveStatus;
      state.count = action.payload.totalElements;
      state.status = RequestStatus.Succeeded;
    });
    builder.addCase(fetchJobsForDashboard.rejected, (state) => {
      state.dashboardJobs = [];
      state.status = RequestStatus.Failed;
    });
  }
});

export const { addJob, upsertJob, dropJobs } = jobsSlice.actions;

export default jobsSlice.reducer;
